import { useTranslate } from '@tolgee/react';
import React, { FC, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import CreateFolderModal from '../modals/folderModal';
import { useDispatch, useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../../pages/lomavisCreator';
import { moveTextCaptionToFolder } from '../../../../../redux/slices/textBlocks';
import { lomavisShowToastr } from '../../../../../utils/ui/toastr';

interface folderTopCardProps {
  folderName: string;
  active: boolean;
  showFolderAdd: boolean;
  loading: boolean;
  icon: string;
  folderUuid?: string; // Add folderUuid property
  isNullFolder?: boolean; // Add isNullFolder property to identify "Not Saved In Folder" option
  handleCardClick: () => void;
}

const FolderTopCard: FC<folderTopCardProps> = ({
  folderName,
  active,
  showFolderAdd,
  loading,
  icon,
  folderUuid,
  isNullFolder,
  handleCardClick
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch<any>();
  const [showCreateModal, setCreateShowModal] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { cloudUuid, locationUuid } = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator
  );

  // Handle drag over event
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    // Only set isDragOver to true if this is not the "All" folder
    if (folderName !== t('lomavisCreator.textBlock.all')) {
      setIsDragOver(true);
    }
  };

  // Handle drag leave event
  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  // Handle drop event
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    // Get the text block UUID from the drop event
    const textBlockUuid = e.dataTransfer.getData('text/plain');

    if (textBlockUuid) {
      // For "Not Saved In Folder" option, set folder to null
      // For "All" option, don't do anything as it's just a filter
      // For regular folders, use the folderUuid
      const targetFolderUuid = isNullFolder ? null : folderUuid;

      // Only proceed if this is a valid drop target (not the "All" filter)
      // The "All" folder doesn't have isNullFolder or folderUuid set
      if (isNullFolder !== undefined && folderName !== t('lomavisCreator.textBlock.all')) {
        dispatch(
          moveTextCaptionToFolder(
            locationUuid,
            cloudUuid,
            textBlockUuid,
            targetFolderUuid,
            () => {
              lomavisShowToastr(
                t('lomavisPostApproval.textBlock.success_captionMoved'),
                'success'
              );
            },
            () => {
              lomavisShowToastr(
                t('lomavisPostApproval.textBlock.err_captionMoved'),
                'error'
              );
            }
          )
        );
      }
    }
  };

  return (
    <div
      className={`d-flex align-items-center justify-content-between w-100 folder-row rounded-1 px-1 cursor-pointer ${isDragOver ? 'bg-light border border-primary' : ''}`}
      tabIndex={0}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div
        onClick={handleCardClick}
        className="d-flex align-items-center gap-2 cursor-pointer w-100"
      >
        <div
          className="position-relative d-flex align-items-center justify-content-center me-2"
          style={{ aspectRatio: '1' }} // `aspectRatio` keeps it square
        >
          <i className={`${icon} fa-duotone fs-2 w-100 mx-auto `}></i>

          {loading && (
            <div
              className="position-absolute d-flex align-items-center justify-content-center m-auto"
              style={{
                inset: 0
              }}
            >
              <div
                className="spinner-border spinner-border-sm text-primary"
                role="status"
              />
            </div>
          )}
        </div>
        <Button
          variant="light"
          size="sm"
          style={{ background: 'none', border: 'none', boxShadow: 'none' }}
          className={` ${
            active ? 'text-primary' : 'text-gray-700'
          } p-0 m-0 fs-6 px-1 w-100 text-start fw-bold`}
        >
          {folderName}
        </Button>
      </div>

      <div
        className="d-flex align-items-center gap-3 position-relative folder-actions"
        ref={dropdownRef}
      >
        {showFolderAdd && (
          <i
            onClick={() => setCreateShowModal(true)}
            className="fa-solid fa-plus cursor-pointer fs-3"
          ></i>
        )}
      </div>
    </div>
  );
};

export default FolderTopCard;
